/**
 * How a reminder reads on the calendar grid, shared by web and native.
 *
 * A reminder is a moment, not a span — it is stored with `endDateMs` equal to
 * `startDateMs` — so the generic event chip's start–end range renders as
 * "4:32 PM – 4:32 PM". These helpers produce the single time and the labelled
 * title a reminder shows instead.
 *
 * Nothing here touches the stored event: the "Reminder: " prefix is presentation
 * only, so renaming a reminder edits the summary and never the prefix.
 *
 * Unrelated to `reminderPresets.ts`, which is about lead-time alerts on ordinary
 * events. The two features share the word and nothing else.
 */

/** The prefix a reminder chip carries, so it reads as a reminder among meetings. */
const REMINDER_TITLE_PREFIX = "Reminder: ";

/**
 * How much of the time grid a reminder draws over.
 *
 * A zero-duration event has no height to be laid out by: the grid collapses it
 * to a sliver, and while dragging it falls back to a one-hour box with the chip
 * filling only the top half of it. Half an hour is the smallest block the grid
 * draws normally, so a reminder shown at that height sits among meetings without
 * claiming time it doesn't hold.
 */
export const REMINDER_DISPLAY_DURATION_MS = 30 * 60 * 1000;

/**
 * The end the grid should draw a reminder to.
 *
 * Presentation only, like the title prefix. What's stored stays a moment
 * (`endDateMs === startDateMs`), and the drag path writes that invariant back
 * rather than persisting the end the grid handed it.
 */
export function reminderChipEnd(start: Date): Date {
  return new Date(start.getTime() + REMINDER_DISPLAY_DURATION_MS);
}

/**
 * The whole label a compact chip shows — "Reminder: Water the ferns, 6:32pm".
 *
 * The time is half the point: it is what distinguishes a reminder's chip from a
 * meeting's, and it is the only place the fire time appears on a grid that
 * otherwise conveys time by position. A chip renderer composing the two itself
 * can drop one without anything noticing.
 */
export function reminderChipLabel(summary: string, start: Date): string {
  return `${reminderChipTitle(summary)}, ${reminderChipTime(start)}`;
}

/**
 * The chip's title. An already-prefixed summary is left alone, so a reminder the
 * user themselves named "Reminder: pay rent" doesn't come out doubled.
 */
export function reminderChipTitle(summary: string): string {
  const trimmed = summary.trim();
  if (trimmed.toLowerCase().startsWith(REMINDER_TITLE_PREFIX.toLowerCase())) {
    return trimmed;
  }
  return `${REMINDER_TITLE_PREFIX}${trimmed}`;
}

/**
 * The single fire time, in the compact form the grid already uses for a short
 * event's start: "9am", "4:32pm". Minutes are dropped on the hour.
 */
export function reminderChipTime(start: Date): string {
  const hours = start.getHours();
  const minutes = start.getMinutes();
  const meridiem = hours >= 12 ? "pm" : "am";
  const hour = hours % 12 || 12;
  return minutes > 0
    ? `${hour}:${minutes.toString().padStart(2, "0")}${meridiem}`
    : `${hour}${meridiem}`;
}
